import React, { createContext, useState, useContext, useEffect } from 'react';
import api, { setAuthToken } from '../utils/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearAuthState = () => {
    setUser(null);
    setToken(null);
    setAuthToken(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  };

  // Check for stored token on app load
  useEffect(() => {
    const storedToken = localStorage.getItem('authToken');

    if (!storedToken) {
      setLoading(false);
      return;
    }

    let ignore = false;

    const restoreSession = async () => {
      try {
        setAuthToken(storedToken);
        const response = await api.get('/auth/me');

        if (ignore) {
          return;
        }

        setToken(storedToken);
        setUser(response.data?.user || null);
        localStorage.setItem('authToken', storedToken);
        localStorage.setItem('user', JSON.stringify(response.data?.user || null));
      } catch (error) {
        console.error('Error restoring session:', error);
        if (!ignore) {
          clearAuthState();
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    restoreSession();

    return () => {
      ignore = true;
    };
  }, []);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    setAuthToken(authToken);
    localStorage.setItem('authToken', authToken);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const updateUser = (userData) => {
    setUser((currentUser) => {
      const nextUser =
        typeof userData === 'function' ? userData(currentUser) : userData;

      if (nextUser) {
        localStorage.setItem('user', JSON.stringify(nextUser));
      } else {
        localStorage.removeItem('user');
      }

      return nextUser;
    });
  };

  const logout = () => {
    clearAuthState();
  };

  const isAuthenticated = !!token;

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, loading, login, updateUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
